'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    // Check if email already exists with a different role
    const { data: existingProfile } = await supabase
      .from('user_profiles')
      .select('role, email, phone')
      .eq('email', email)
      .maybeSingle();

    if (existingProfile) {
      const incomingRole = metadata?.role || 'customer';
      if (existingProfile.role !== incomingRole) {
        throw new Error(
          `This email is already registered as a ${existingProfile.role}. One account can only have one role. Please use a different email.`
        );
      }
      throw new Error('An account with this email already exists. Please sign in instead.');
    }

    // Check if phone already exists with a different role (if phone provided)
    if (metadata?.phone) {
      const { data: phoneProfile } = await supabase
        .from('user_profiles')
        .select('role, phone')
        .eq('phone', metadata.phone)
        .maybeSingle();

      if (phoneProfile) {
        const incomingRole = metadata?.role || 'customer';
        if (phoneProfile.role !== incomingRole) {
          throw new Error(
            `This phone number is already registered as a ${phoneProfile.role}. One account can only have one role. Please use a different phone number.`
          );
        }
        throw new Error('An account with this phone number already exists. Please sign in instead.');
      }
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
